import PropTypes from "prop-types";

import {
  Checkbox, fieldsetProps, getCaptchaErrorMessage, getCaptchaSiteKey, getGuestAccountRegisterFailed, merge, newFieldProps, newGetFieldLabel,
  newGetFieldPlaceholder,
  newGetSelectFieldOptions, newIsFieldRequired, Translation as T, useAmsSelector,
  useCaptcha
} from "@awe-web/shared/lib";
import TextWithCmsLink from "../../../components/cms_overlay";
import {
  Button,
  ErrorMessage,
  RequiredNotice,
  ScrollToError
} from "../../../components/form.js";
import { Col, Row } from "../../../components/grid.js";
import { ScreenSectionHeading } from "../../../components/screen.js";
import { AccountFieldset, AddressFieldset, ConsentFieldset, IdentificationAndDriversLicenseFieldset } from "../../../user/components/form.js";

import { useCallback, useEffect } from "react";
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useSelector } from "react-redux";
import "./sign_up_form.scss";

const TITLE_LEGAL = "FSD0000197";
const ADDITION_TO_TERMS_AND_CONDITIONS = "FSD0000275";
const NOTICE = "FSD0000209";
const CONTINUE = "FSD0000135";

export default function SignUpGuestForm(props) {
  const { handleSubmit, hasField, onCaptchaChange } = props;
  const fieldProps = newFieldProps(props);
  const error = name => fieldProps(name).error;

  const getFieldLabel = useAmsSelector((state) => newGetFieldLabel("guestRegistration")(state));
  const isFieldRequired = useAmsSelector((state) => newIsFieldRequired("guestRegistration")(state));
  const getFieldPlaceholder = useAmsSelector((state) => newGetFieldPlaceholder("guestRegistration")(state));
  const getSelectFieldOptions = useAmsSelector((state) => newGetSelectFieldOptions("guestRegistration")(state));


  const signUpErrorMessage = useSelector((state) => getCaptchaErrorMessage(state));
  const isGuestAccountNotUniqueError = useSelector((state) => getGuestAccountRegisterFailed(state));
  const captchaPublicSiteKey = useSelector((state) => getCaptchaSiteKey(state));
  const { captchaRef, handleCaptcha, currentLanguage } = useCaptcha();

  const handleCaptchaChange = useCallback((token) => {
    onCaptchaChange(token);
    handleCaptcha(token);
  }, [handleCaptcha, onCaptchaChange]);

  useEffect(() => {
    if (signUpErrorMessage || isGuestAccountNotUniqueError >= 0) {
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }

    }

  }, [captchaRef, signUpErrorMessage, isGuestAccountNotUniqueError]);

  return (
    <form className="sign-up-form" onSubmit={handleSubmit}>
      <ScrollToError />
      <AccountFieldset
        {...fieldsetProps(props)}
        hasField={hasField}
        getFieldLabel={getFieldLabel}
        isFieldRequired={isFieldRequired}
        getFieldPlaceholder={getFieldPlaceholder}
        getSelectFieldOptions={getSelectFieldOptions}
        isGuestMode={true}
      />
      <AddressFieldset {...fieldsetProps(props)}
        hasField={hasField}
        getFieldLabel={getFieldLabel}
        isFieldRequired={isFieldRequired}
        getFieldPlaceholder={getFieldPlaceholder}
        getSelectFieldOptions={getSelectFieldOptions} />
      {/* {creditCardsEnabled && (
        <CreditCardsFieldset {...fieldsetProps(props)} />
      )} */}
      <IdentificationAndDriversLicenseFieldset
        className="sign-up-confirmed-form__identification-and-drivers-license-fieldset"
        {...fieldsetProps(props)}
        hasField={hasField}
        getFieldLabel={getFieldLabel}
        isFieldRequired={isFieldRequired}
        getFieldPlaceholder={getFieldPlaceholder}
        getSelectFieldOptions={getSelectFieldOptions}
      />
      <Row>
        <Col
          desktop={{ cols: 8, offset: 2 }}
          className="sign-up-form__form-section--legal"
        >
          <ScreenSectionHeading light><T code={TITLE_LEGAL} /></ScreenSectionHeading>
          <div className="sign-up-form__field">
            <label className={error("tosAccepted") ? 'labeled-checkbox labeled-checkbox--error' : 'labeled-checkbox'}>
              <Checkbox
                {...fieldProps("tosAccepted")}
                className="labeled-checkbox__input"
              />
              <div>
                <TextWithCmsLink className="sign-up-form__terms-and-conditions-label checkbox-label" pageName="agb">
                  {getFieldLabel("tosAccepted")}
                </TextWithCmsLink>
                <T code={ADDITION_TO_TERMS_AND_CONDITIONS} />
              </div>
            </label>
            <ErrorMessage error={error("tosAccepted")} />
          </div>
        </Col>
      </Row>

      {signUpErrorMessage && <Row>
        <Col
          tablet={{ cols: 4, offset: 4 }}
          desktop={{ cols: 4, offset: 6 }}
          className="sign-up-form__form-buttons__col--submit-button"
        >
          <ErrorMessage error={signUpErrorMessage} />
        </Col>

      </Row>
      }
      {captchaPublicSiteKey && <Row className="sign-up-form__field">
        <Col
          tablet={{ cols: 4, offset: 4 }}
          desktop={{ cols: 4, offset: 6 }}
          className="sign-up-form__form-buttons__col--submit-button"
        >
          <HCaptcha
            ref={captchaRef}
            sitekey={captchaPublicSiteKey}
            onVerify={handleCaptchaChange}
            hl={currentLanguage}
          />
        </Col>
      </Row>
      }
      <Row>
        <Col
          tablet={{ cols: 4, offset: 4 }}
          desktop={{ cols: 4, offset: 6 }}
          className="sign-up-form__form-buttons__col--submit-button"
        >
          <Button submit continue primary onClick={handleSubmit} className="sign-up-form__submit-button">
            <T code={CONTINUE} />
          </Button>
        </Col>
      </Row>

      <Row className="sign-up-form__required-notice">
        <Col desktop={{ cols: 8, offset: 2 }}>
          <RequiredNotice><T code={NOTICE} /></RequiredNotice>
        </Col>
      </Row>
    </form>
  );
}

SignUpGuestForm.valuesShape = merge(
  AccountFieldset.valuesShape,
  ConsentFieldset.valuesShape,
  {
    "tosAccepted": PropTypes.bool
  }
);

SignUpGuestForm.propTypes = {
  values: PropTypes.shape(SignUpGuestForm.valuesShape),
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleSubmit: PropTypes.func
};

